import * as React from "react";
import { cn } from "./utils";

type TextareaProps = React.ComponentProps<"textarea"> & {
  /**
   * When true, the textarea grows to fit its content (no inner scrollbar) and
   * shrinks back as text is removed. Honors `min-h-*` classes as a floor.
   */
  autosize?: boolean;
};

function Textarea({ className, autosize, onInput, rows, value, defaultValue, ...props }: TextareaProps) {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  const resize = React.useCallback(() => {
    const el = ref.current;
    if (!el || !autosize) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autosize]);

  // Grow/shrink on controlled value changes (and initial mount).
  React.useLayoutEffect(() => {
    resize();
  }, [resize, value, defaultValue]);

  return (
    <textarea
      data-slot="textarea"
      ref={ref}
      /**
       * AN AUTOSIZING TEXTAREA FLOORS AT ONE ROW, not at the HTML default of
       * two — and this is the fix for a real, visible bug, not a tidy-up.
       *
       * `resize()` measures by setting `height:auto` and reading `scrollHeight`.
       * For a textarea, `height:auto` resolves to the `rows` box, and
       * `scrollHeight` is never smaller than that box. So with the default
       * `rows=2`, a ONE-LINE value measured two lines tall and the element was
       * then pinned there. Measured on the peek masthead: clicking the title
       * grew the header by a full line to make room for an empty second row that
       * could never be filled — "the title when clicked gets larger" (Matt,
       * 2026-07-21). Autosize means the CONTENT decides the height, so the floor
       * has to be the smallest thing content can be.
       *
       * An explicit `rows` still wins, and a `min-h-*` class still floors it
       * (min-height beats the inline height), so both existing escape hatches
       * are intact — the task peek's `min-h-32` description keeps its floor.
       */
      rows={rows ?? (autosize ? 1 : undefined)}
      value={value}
      defaultValue={defaultValue}
      onInput={(e) => {
        resize();
        onInput?.(e);
      }}
      className={cn(
        "w-full min-w-0 rounded-md border border-foreground/15 bg-background px-3 py-2 type-body text-foreground transition-[color,box-shadow] outline-none placeholder:text-muted-foreground hover:border-foreground/25 focus-visible:border-foreground/40 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:type-body",
        autosize ? "resize-none overflow-hidden" : "min-h-20",
        "aria-invalid:border-destructive",
        className,
      )}
      {...props}
    />
  );
}

export { Textarea };
